import {ApiRepository} from '../base/api-repository';
import {Capacitor} from '@capacitor/core';
import {
    PushNotifications,
    PushNotificationSchema,
    ActionPerformed,
    Token,
} from '@capacitor/push-notifications';
import {UserStore} from './user-store';
import {routes} from '../base/routes';
import {RouterStore} from 'mobx-router';
import {RootStore} from '../base/root-store';
import * as Sentry from '@sentry/react';
import {FCM} from '@capacitor-community/fcm';
import {RegistrationError} from '@capacitor/push-notifications/dist/esm/definitions';
import {App} from '@capacitor/app';

export class PushNotificationStore {

    constructor(private api: ApiRepository, private userStore: UserStore, private router: RouterStore<RootStore>) {
        if (this.isSupported()) {
            this.registerPushNotifications();
            this.updateFcmTokenOnAppStateActive();
        }
    }

    updateFcmTokenOnAppStateActive() : void {
        if (Capacitor.getPlatform() === 'ios') {
            App.addListener('appStateChange', async (state) => {
                if (state.isActive && this.userStore.state === 'user-loaded') {
                    const permissions = await PushNotifications.checkPermissions();

                    if (permissions.receive === 'granted') {
                        this.updateFcmToken();
                    }
                }
            });
        }
    }

    isSupported(): boolean {
        return Capacitor.getPlatform() !== 'web';
    }

    async registerPushToken(): Promise<void> {
        const permissions = await PushNotifications.checkPermissions();

        if (permissions.receive === 'prompt' || permissions.receive === 'prompt-with-rationale') {
            const requestResult = await PushNotifications.requestPermissions();

            if (requestResult.receive === 'granted') {
                await PushNotifications.register();
            } else {
                console.error('User rejected push notification permissions');

                return;
            }
        } else if (permissions.receive === 'granted') {
            await PushNotifications.register();
        } else if (permissions.receive === 'denied') {
            console.error('Push permissions are denied');
        }

        // On success
        if (Capacitor.getPlatform() === 'android') {
            PushNotifications.addListener('registration',
                (token: Token) => {
                    const employeeId = this.userStore.payload!.employeeId;

                    this.api.post(`/push-notifications/employee/${employeeId}/register`, {pushToken: token.value});
                },
            );
        } else {
            PushNotifications.addListener('registration',
                () => {
                    this.updateFcmToken();
                },
            );
        }

        // On error
        PushNotifications.addListener('registrationError',
            (error: RegistrationError) => {
                Sentry.captureException(error.error);
                console.error(error.error);
            },
        );
    }

    async updateFcmToken() {
        FCM.getToken()
            .then((result) => {
                if (result.token) {
                    console.log(`Current FCM token: ${result.token}`);
                    this.sendTokenToBackend(result.token);
                } else {
                    console.log('No FCM token available. Request permission to generate one.');
                }
            })
            .catch((err) => {
                console.error('An error occurred while retrieving the FCM token:', err);
            });
    }

    async sendTokenToBackend(token: string) {
        const employeeId = this.userStore.payload!.employeeId;

        this.api.post(`/push-notifications/employee/${employeeId}/register`, { pushToken: token })
            .then(() => console.log('FCM token updated successfully on the backend.'))
            .catch((error) => console.error('Error sending FCM token to backend:', error));
    }

    registerPushNotifications(): void {

        // Show us the notification payload if the app is open on our device
        PushNotifications.addListener('pushNotificationReceived',
            (notification: PushNotificationSchema) => {
                if (notification.data.question === undefined) {
                    this.router.goTo(routes.chatAnswerWeeklyQuestions, {employeeId: notification.data.employeeId}, {token: notification.data.token});
                } else {
                    this.router.goTo(routes.chatAskQuestion, {questionId: notification.data.question}, {token: notification.data.token});
                }
            },
        );

        // Method called when tapping on a notification
        PushNotifications.addListener('pushNotificationActionPerformed',
            (performed: ActionPerformed) => {
                if (performed.notification.data.question === undefined) {
                    this.router.goTo(routes.chatAnswerWeeklyQuestions, {employeeId: performed.notification.data.employeeId}, {token: performed.notification.data.token});
                } else {
                    this.router.goTo(routes.chatAskQuestion, {questionId: performed.notification.data.question}, {token: performed.notification.data.token});
                }
            },
        );
    }
}
